import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS, Step, Styles, Locale } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../contexts/TourContext';
import { useTranslation } from 'react-i18next';
import { createCustomTooltip } from './CustomTooltip';

export interface BaseTourProps {
    isRunning: boolean;
    onComplete: () => void;
    tourId: string;
    steps: Step[];
    navigateTo?: string;
    customCallback?: (data: CallBackProps, defaultHandler: (data: CallBackProps) => void) => void;
    customLocale?: Locale;
}

export const defaultTourStyles: Partial<Styles> = {
    options: {
        primaryColor: '#2a6355',
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        overlayColor: 'rgba(0, 0, 0, 0.4)',
        spotlightShadow: '0 0 15px rgba(42, 99, 85, 0.3)',
        beaconSize: 36,
        zIndex: 10000,
    },
    tooltip: {
        borderRadius: 12,
        fontSize: 14,
        padding: 20,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e7eb',
    },
    tooltipContainer: {
        textAlign: 'left',
    },
    tooltipTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: 8,
    },
    tooltipContent: {
        fontSize: 14,
        lineHeight: 1.5,
        color: '#6b7280',
        marginBottom: 16,
    },
    tooltipFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 16,
    },
    buttonNext: {
        backgroundColor: '#2a6355',
        borderRadius: 8,
        color: '#ffffff',
        fontSize: 14,
        fontWeight: 500,
        padding: '10px 20px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    } as React.CSSProperties,
    buttonBack: {
        backgroundColor: 'transparent',
        borderRadius: 8,
        color: '#6b7280',
        fontSize: 14,
        fontWeight: 500,
        padding: '10px 20px',
        border: '1px solid #d1d5db',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    } as React.CSSProperties,
    buttonSkip: {
        backgroundColor: 'transparent',
        borderRadius: 8,
        color: '#6b7280',
        fontSize: 14,
        fontWeight: 500,
        padding: '10px 20px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    } as React.CSSProperties,
    spotlight: {
        borderRadius: 8,
    },
};

export const useDefaultTourLocale = (): Locale => {
    const { t } = useTranslation();

    return {
        back: t('back'),
        close: t('close'),
        last: t('finish'),
        next: t('next'),
        skip: t('skip_tour'),
    };
};

const CustomTooltip = createCustomTooltip('#2a6355');

const BaseTour: React.FC<BaseTourProps> = ({ isRunning, onComplete, tourId, steps, navigateTo, customCallback, customLocale }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentTour, stopTour } = useTour();
    const [stepIndex, setStepIndex] = useState(0);
    const [run, setRun] = useState(false);
    const defaultTourLocale = useDefaultTourLocale();

    const scrollToTarget = useCallback((target: string | HTMLElement) => {
        const performScroll = (useSmooth = true) => {
            let element: HTMLElement | null = null;

            if (typeof target === 'string') {
                if (target === 'body') {
                    window.scrollTo({ top: 0, behavior: useSmooth ? 'smooth' : 'auto' });
                    return;
                }
                element = document.querySelector(target) as HTMLElement;
            } else {
                element = target;
            }

            if (element) {
                const rect = element.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const offset = 100;
                const targetPosition = rect.top + scrollTop - offset;

                window.scrollTo({
                    top: targetPosition,
                    behavior: useSmooth ? 'smooth' : 'auto',
                });
            }
        };

        performScroll(false);
        setTimeout(() => {
            performScroll(true);
        }, 50);
    }, []);

    useEffect(() => {
        if (isRunning && currentTour === tourId) {
            setRun(true);
            setStepIndex(0);
            if (navigateTo && !location.pathname.startsWith(navigateTo)) {
                navigate(navigateTo);
            }
        }
    }, [isRunning, currentTour, tourId, navigate, navigateTo, location.pathname]);

    useEffect(() => {
        if (run && steps.length > 0 && stepIndex < steps.length) {
            const currentStep = steps[stepIndex];
            if (currentStep && currentStep.target && currentStep.target !== 'body') {
                const timer = setTimeout(() => {
                    scrollToTarget(currentStep.target as string);
                }, 800);
                return () => clearTimeout(timer);
            }
        }
    }, [run, stepIndex, steps, scrollToTarget]);

    const defaultCallback = (data: CallBackProps) => {
        const { status, type, action, index, step } = data;

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TOOLTIP) {
            if (step && step.target && step.target !== 'body') {
                setTimeout(() => {
                    scrollToTarget(step.target as string);
                }, 600);
            } else if (step && step.target === 'body') {
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 600);
            }
        }

        if (type === EVENTS.TARGET_NOT_FOUND) {
            setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
            setStepIndex(index + 1);
        } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
            setStepIndex(index - 1);
        } else if (type === EVENTS.TOUR_END || ([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
            setRun(false);
            stopTour();
            localStorage.setItem(`tour_completed_${tourId}`, 'true');
            onComplete();
        }
    };

    const handleCallback = (data: CallBackProps) => {
        if (customCallback) {
            customCallback(data, defaultCallback);
        } else {
            defaultCallback(data);
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            callback={handleCallback}
            continuous={true}
            showProgress={true}
            showSkipButton={true}
            styles={defaultTourStyles}
            locale={customLocale || defaultTourLocale}
            disableOverlayClose={true}
            hideCloseButton={false}
            disableScrolling={true}
            scrollToFirstStep={false}
            tooltipComponent={CustomTooltip}
        />
    );
};

export default BaseTour;
